import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  MenuOutlined,
  SearchOutlined,
  UserOutlined,
  CalendarOutlined,
  LogoutOutlined,
  BellOutlined,
  HomeOutlined,
  BulbOutlined,
  BulbFilled,
  LeftOutlined,
  CheckOutlined,
  DownOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  TeamOutlined,
  PushpinOutlined,
  PushpinFilled,
} from '@ant-design/icons';
import { Dropdown, Badge, Tooltip, notification, Drawer } from 'antd';
import { useAuth } from '../../assets/utils/useAuth';
import { usePermissions } from '../../assets/utils/usePermissions';
import { LEGACY_MODULE_ROLE_IDS } from '../../assets/utils/accessCatalog';
import Helpers from '../../assets/utils/Helpers';
import lib from '../../assets/utils/lib';
import { add_client } from '../../stores/verify';
import { toggleCollapse, setNotificationData } from '../../stores/sidebar';
import logo from '../../assets/images/claima_black.png';
import collapsedLogo from '../../assets/images/claima.png';
import { useNotificationContext } from '../../components/Notifications/NotificationContext';
import NotificationsDropdown from '../../components/Notifications/NotificationsDropdown';
import { useCountersEnhanced } from '../../hooks/useCountersEnhanced';
import { useGlobalSearch } from '../../contexts/GlobalSearchContext';
import { usePageHeader } from '../../contexts/PageHeaderContext';
import usePendingPlanRequestsCount from '../orgs/hooks/usePendingPlanRequestsCount';
import usePendingStaffUploadsCount from '../orgs/hooks/usePendingStaffUploadsCount';
import useEnrollmentConflictsCount from '../orgs/hooks/useEnrollmentConflictsCount';
import { canManagePlanRequests, canManageStaffUploads } from '../orgs/planRequestHelpers';
import { canManageEnrollmentConflicts } from '../orgs/enrollmentConflictHelpers';
import StaffProfileEditor from '../dash/StaffProfileEditor';
import { fetchCurrentUserProfile, updateOwnProfile, changeOwnPassword } from '../dash/rbacApi';
import { useNavigationTree } from './useNavigationTree';
import { flattenNavItems } from './navigationConfig';
import { usePinnedNavItems } from './usePinnedNavItems';
import './sidebar.css'; // nav items: background/color only — no accents/borders
import { NotificationBell, useNotify } from '../notify';

/**
 * Legacy staff-role ids the sidebar consults while the RBAC map is still empty.
 *
 * Shared with ProtectedRoute through LEGACY_MODULE_ROLE_IDS, with the one divergence the
 * catalog documents: the nav has always shown "Pending claims" to the whole legacy claims
 * family, while ProtectedRoute only ever let the super user onto the route. Keeping the
 * sidebar's own value here means no nav item changes visibility during this migration.
 *
 * Anything not listed (including catalog-injected modules) falls back to the super user
 * only, which is what the old `roleId === 1 => show everything` short-circuit did.
 */
const SIDEBAR_LEGACY_SUPER_ONLY = [1];
const SIDEBAR_LEGACY_MODULE_ROLE_IDS = {
  ...LEGACY_MODULE_ROLE_IDS,
};

const sidebarLegacyRoleIds = (moduleKey) =>
  SIDEBAR_LEGACY_MODULE_ROLE_IDS[moduleKey] || SIDEBAR_LEGACY_SUPER_ONLY;

const badgeTotal = (badgeCount) => {
  if (typeof badgeCount === 'number') return Math.max(0, badgeCount);
  if (badgeCount && typeof badgeCount === 'object') {
    return Math.max(0, Number(badgeCount.primary) || 0)
      + Math.max(0, Number(badgeCount.secondary) || 0);
  }
  return 0;
};

const Sidebar = () => {
  // Notification System v2: once switched on, its bell replaces the old dropdown.
  const notifyV2 = useNotify();
  const notifyV2Enabled = !!notifyV2?.state?.enabled;
  const { user, set, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [ownProfile, setOwnProfile] = useState(null);

  const { collapsed, notificationData } = useSelector((state) => state.sidebar || {
    collapsed: false,
    notificationData: { new: 0, updated: 0 }
  });

  // Get notification state from context (for navbar button)
  // The hook returns safe defaults if context is not available
  const {
    connected,
    unreadCount,
  } = useNotificationContext();

  // Get auth token for counters
  const authToken = user?.jwt || user?.token || user?.access_token || null;

  // Get counters for badges (PA, Claims, etc.)
  // Using counters endpoint instead of unverified_no endpoint
  const { counters } = useCountersEnhanced(authToken, {
    adaptive: true,
    fastInterval: 15000,   // 15s when active (increased from 5s)
    slowInterval: 60000,   // 60s when idle (increased from 30s)
    idleTimeout: 120000,    // 2min idle = slow mode (increased from 1min)
  });

  // State for horizontal navbar notification button dropdown
  const [showNavbarNotificationsDropdown, setShowNavbarNotificationsDropdown] = useState(false);

  // Close navbar dropdown when clicking outside
  useEffect(() => {
    if (!showNavbarNotificationsDropdown) return;

    const handleClickOutside = (event) => {
      const notificationBtn = event.target.closest('.horizontal-notification-wrapper');
      const dropdown = event.target.closest('.notifications-dropdown');

      if (!notificationBtn && !dropdown) {
        setShowNavbarNotificationsDropdown(false);
      }
    };

    // Add listener after a short delay to avoid immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNavbarNotificationsDropdown]);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [viewport, setViewport] = useState('desktop'); // mobile | tablet | desktop
  const [openGroup, setOpenGroup] = useState(null);
  const [currentData, setCurrentData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('sidebarDarkMode');
    return saved ? JSON.parse(saved) : false;
  });
  // NOTE: Removed 'focus' state - no longer needed since we use counters endpoint

  const isMobile = viewport === 'mobile';
  const isTablet = viewport === 'tablet';
  // Tablet always shows icon rail; desktop respects user collapse; mobile drawer shows labels when open
  const railCollapsed = isMobile ? false : (isTablet || collapsed);

  useEffect(() => {
    const mqMobile = window.matchMedia('(max-width: 767px)');
    const mqTablet = window.matchMedia('(min-width: 768px) and (max-width: 1023px)');
    const syncViewport = () => {
      if (mqMobile.matches) setViewport('mobile');
      else if (mqTablet.matches) setViewport('tablet');
      else setViewport('desktop');
    };
    syncViewport();
    mqMobile.addEventListener('change', syncViewport);
    mqTablet.addEventListener('change', syncViewport);
    return () => {
      mqMobile.removeEventListener('change', syncViewport);
      mqTablet.removeEventListener('change', syncViewport);
    };
  }, []);

  // Close drawer when leaving mobile / on route change; lock body scroll while open
  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen || !isMobile) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [mobileOpen, isMobile]);

  const {
    canAccess: checkModuleAccess,
    hasPermission,
    hasAnyPermission,
    canStayOnModule,
  } = usePermissions();
  const { open: openGlobalSearch, canSearch: canGlobalSearch } = useGlobalSearch();
  const { header: pageHeader } = usePageHeader();
  const { pinnedKeys, isPinned, togglePin } = usePinnedNavItems();

  // Badge visibility follows module access from the RBAC catalog.
  const canSeePABadges = checkModuleAccess('preauth');
  const canSeeClaimsBadges = checkModuleAccess('claims');
  const preferEnrolleeCounters = hasAnyPermission(['clients.update', 'preauth.decide'])
    && !hasAnyPermission(['clients.read_all', 'corporations.read', 'corporations.read_all']);
  const preferClientCounters = hasAnyPermission(['clients.read_all', 'corporations.read', 'corporations.read_all']);

  const canSeePlanRequests = useMemo(
    () => canManagePlanRequests(user, hasAnyPermission),
    [user, hasAnyPermission],
  );
  const canSeeStaffUploads = useMemo(
    () => canManageStaffUploads(user, hasAnyPermission),
    [user, hasAnyPermission],
  );
  const canSeeEnrollmentConflicts = useMemo(
    () => canManageEnrollmentConflicts(user, hasAnyPermission),
    [user, hasAnyPermission],
  );
  const { count: pendingPlanRequestsCount } = usePendingPlanRequestsCount(Boolean(user && canSeePlanRequests));
  const { count: pendingStaffUploadsCount } = usePendingStaffUploadsCount(Boolean(user && canSeeStaffUploads));
  const { open: openEnrollmentConflicts } = useEnrollmentConflictsCount(Boolean(user && canSeeEnrollmentConflicts));

  // Nav tree: the backend access-module catalog when it is live, the hardcoded tree in
  // navigationConfig.js when it is not. Shared with the shell breadcrumb via the same hook.
  const navigationConfig = useNavigationTree();

  // Flatten pinned items into a synthetic "Pinned" group shown above everything else.
  const pinnedGroup = useMemo(() => {
    if (pinnedKeys.length === 0) return null;
    const items = navigationConfig
      .flatMap((group) => flattenNavItems(group.items))
      .filter((item) => pinnedKeys.includes(item.key));
    return items.length > 0 ? { group: 'Pinned', items } : null;
  }, [navigationConfig, pinnedKeys]);

  // Permission-first nav: module from the access catalog; canStayOnModule applies
  // the legacy role ids only while the RBAC map is empty.
  const hasAccess = useCallback((item) => {
    if (item.module) {
      return canStayOnModule(item.module, sidebarLegacyRoleIds(item.module));
    }
    if (item.permission) {
      return hasPermission(item.permission);
    }
    return false;
  }, [canStayOnModule, hasPermission]);

  // NOTE: Removed fetchNotificationData and lib.unverified calls
  // We now use the counters endpoint via useCountersEnhanced hook above
  // The counters provide new/updated counts for clients/enrollees which replaces unverified_no
  
  // Update currentData from counters for backward compatibility with existing code
  useEffect(() => {
    if (counters) {
      let newData = { new: 0, updated: 0 };

      if (preferClientCounters) {
        newData = {
          new: counters?.clients?.new || 0,
          updated: counters?.clients?.updated || 0,
        };
      } else if (preferEnrolleeCounters) {
        newData = {
          new: counters?.enrollees?.new || 0,
          updated: counters?.enrollees?.updated || 0,
        };
      }

      setCurrentData(newData);
      // Update Redux store with notification data (for backward compatibility)
      dispatch(setNotificationData(newData));
    }
  }, [counters, preferClientCounters, preferEnrolleeCounters, dispatch]);

  // Dark mode toggle handler
  const toggleDarkMode = useCallback(() => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('sidebarDarkMode', JSON.stringify(newDarkMode));

    // Dispatch custom event for other components to listen to
    window.dispatchEvent(new CustomEvent('themeChanged', {
      detail: { darkMode: newDarkMode }
    }));

    notification.info({
      message: newDarkMode ? 'Dark Mode Enabled' : 'Light Mode Enabled',
      description: `Switched to ${newDarkMode ? 'dark' : 'light'} mode`,
      placement: 'topRight',
      duration: 2
    });
  }, [darkMode]);

  // Enhanced logout with confirmation
  const handleLogout = () => {
    notification.info({
      message: 'Logging out...',
      description: 'Please wait while we sign you out',
      placement: 'topRight',
      duration: 1
    });

    setTimeout(() => {
      Helpers.logout(set, dispatch);
      navigate('/login');
    }, 1000);
  };

  const openOwnProfileDrawer = useCallback(async () => {
    setProfileDrawerOpen(true);
    try {
      const response = await fetchCurrentUserProfile();
      const profilePayload = response?.data || response;
      const staffProfile =
        profilePayload?.user?.profile
        || profilePayload?.profile
        || profilePayload
        || {};
      setOwnProfile({
        staffId: staffProfile.staffId || staffProfile.staff_id || user?.staffId,
        full_name: staffProfile.full_name || staffProfile.fullName || staffProfile.name || '',
        email: staffProfile.email || user?.email || '',
        phone: staffProfile.phone || '',
      });
    } catch (error) {
      const fallback = user?.profile?.user?.profile || user?.profile?.profile || {};
      setOwnProfile({
        staffId: fallback.staffId || user?.staffId,
        full_name: fallback.full_name || fallback.fullName || fallback.name || '',
        email: fallback.email || user?.email || '',
        phone: fallback.phone || '',
      });
    }
  }, [user]);

  const handleSaveOwnProfile = async (values) => {
    setProfileSaving(true);
    try {
      const payload = {
        email: String(values.email || '').trim().toLowerCase(),
        full_name: String(values.full_name || values.fullName || values.name || '').trim(),
        phone: String(values.phone || '').trim(),
      };
      const result = await updateOwnProfile(payload);
      if (result?.error) {
        notification.error({
          message: result?.message || 'Failed to update profile',
          placement: 'topRight',
        });
        return;
      }

      const newPassword = String(values.password || '');
      if (newPassword) {
        const passwordResult = await changeOwnPassword({
          current_password: String(values.current_password || ''),
          password: newPassword,
          confirm_password: String(values.confirm_password || ''),
        });
        if (passwordResult?.error) {
          notification.error({
            message: passwordResult?.message || 'Profile saved, but password was not changed',
            placement: 'topRight',
          });
          return;
        }
      }

      notification.success({
        message: newPassword ? 'Profile and password updated' : 'Profile updated',
        description: 'Your email and contact details were saved. Use the new email next time you sign in.',
        placement: 'topRight',
      });
      setProfileDrawerOpen(false);
      if (typeof refreshProfile === 'function') {
        await refreshProfile();
      }
    } catch (error) {
      notification.error({
        message:
          error?.response?.data?.message
          || error?.message
          || 'Failed to update profile',
        placement: 'topRight',
      });
    } finally {
      setProfileSaving(false);
    }
  };

  // Enhanced user dropdown menu
  const userMenuItems = [
    {
      key: 'profile',
      label: 'Profile Settings',
      icon: <UserOutlined />,
      onClick: () => {
        openOwnProfileDrawer();
      }
    },
    {
      key: 'notifications',
      label: 'Notifications',
      icon: <BellOutlined />,
      onClick: () => {
        navigate('/notifications');
      }
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: handleLogout,
      danger: true
    }
  ];

  // Enhanced toggle functions
  const toggleCollapseHandler = useCallback(() => {
    if (isMobile || isTablet) return;
    dispatch(toggleCollapse());
  }, [dispatch, isMobile, isTablet]);

  const toggleMobileMenu = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  // Enhanced navigation with loading state
  const navigateToRoute = useCallback((path) => {
    const [pathname, search = ''] = String(path || '').split('?');
    if (pathname === location.pathname && search === (location.search || '').replace(/^\?/, '')) {
      return;
    }

    setIsLoading(true);
    navigate(path);
    setMobileOpen(false);

    // Reset loading after navigation
    setTimeout(() => setIsLoading(false), 500);
  }, [navigate, location.pathname, location.search]);

  // Check if route is active (exact match; partners detail URLs stay under /partners/:id)
  const isActiveRoute = useCallback((path) => {
    const [pathname, search] = String(path || '').split('?');
    // An item that names a query string (Campaigns → Push / Email) is active only on
    // exactly that query; its parent, which names none, is active on any.
    if (search !== undefined) {
      return location.pathname === pathname
        && (location.search || '').replace(/^\?/, '') === search;
    }
    // Organizations product: directory, company detail, and enrollment work queues.
    if (pathname === '/corporations') {
      return location.pathname === '/corporations'
        || location.pathname.startsWith('/corporations/users/')
        || location.pathname === '/corporations/staff-uploads'
        || location.pathname === '/corporations/enrollment-holds'
        || location.pathname === '/corporations/enrollment-conflicts';
    }
    if (pathname === '/plans') {
      return location.pathname === '/plans'
        || location.pathname === '/corporations/plan-requests'
        || location.pathname === '/settings/enrollment-segments'
        || location.pathname === '/admin/enrollment-segments';
    }
    if (location.pathname === pathname) return true;
    if (
      pathname === '/admin/integrations/partners' &&
      (location.pathname.startsWith('/admin/integrations/partners/') ||
        location.pathname === '/admin/integrations/saaspro')
    ) {
      return true;
    }
    return false;
  }, [location.pathname, location.search]);

  // Keep the category that owns the current page open (exclusive accordion).
  useEffect(() => {
    const activeGroup = navigationConfig.find((group) =>
      flattenNavItems(group.items).some((item) => isActiveRoute(item.path)),
    );
    if (activeGroup) {
      setOpenGroup(activeGroup.group);
    }
    // navigationConfig is a dependency because the catalog can rename or re-group the
    // category that owns the current page after the sidebar has already rendered.
  }, [navigationConfig, location.pathname, location.search, isActiveRoute]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get badge data for a navigation item.
  // Returns either:
  // - number (single badge)
  // - object { primary, secondary, hasAny } for multi-badge items
  const getBadgeCount = useCallback((item) => {
    if (item.key === 'plans' && canSeePlanRequests) {
      return pendingPlanRequestsCount > 0 ? pendingPlanRequestsCount : 0;
    }

    // Organizations badge = enrollment work waiting across companies
    if (item.key === 'orgs' && (canSeeStaffUploads || canSeeEnrollmentConflicts)) {
      const total = (canSeeStaffUploads ? pendingStaffUploadsCount : 0)
        + (canSeeEnrollmentConflicts ? openEnrollmentConflicts : 0);
      return total > 0 ? total : 0;
    }

    if (!counters) return 0;

    switch (item.key) {
      case 'preauth': {
        // Pending + queried PA for users with preauth access
        if (!canSeePABadges) return 0;
        const pending = counters?.pa?.pending || 0;
        const queried = counters?.pa?.queried || 0;
        return {
          primary: pending,
          secondary: queried,
          hasAny: pending > 0 || queried > 0,
        };
      }

      case 'claims':
        // Pending claims count for users with claims access
        if (canSeeClaimsBadges) {
          const pendingReview = counters?.claims?.pending_review || 0;
          return pendingReview;
        }
        return 0;

      case 'client': {
        // New / updated clients (if counters have this shape)
        if (!item.badge) return 0;
        let primary = 0;
        let secondary = 0;

        if (preferEnrolleeCounters) {
          primary = counters?.enrollees?.new || 0;
          secondary = counters?.enrollees?.updated || 0;
        } else if (preferClientCounters) {
          primary = counters?.clients?.new || 0;
          secondary = counters?.clients?.updated || 0;
        }

        return {
          primary,
          secondary,
          hasAny: primary > 0 || secondary > 0,
        };
      }

      case 'encounters':
        // Reserved for future multi-badge encounters
        return 0;

      case 'appointments':
        // Example: pending_review (adjust when backend shape is final)
        const pendingReview = counters?.appointments?.pending_confirmation || 0;
        const upcoming = counters?.appointments?.today || 0;
        return {
          primary: pendingReview,
          secondary: upcoming,
          hasAny: pendingReview > 0 || upcoming > 0,
        };


      default:
        return 0;
    }
  }, [counters, canSeePABadges, canSeeClaimsBadges, preferEnrolleeCounters, preferClientCounters, pendingPlanRequestsCount, canSeePlanRequests, pendingStaffUploadsCount, canSeeStaffUploads, openEnrollmentConflicts, canSeeEnrollmentConflicts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Enhanced navigation item renderer
  const renderNavItem = useCallback((item) => {
    if (!hasAccess(item)) return null;

    const isActive = isActiveRoute(item.path);
    const childItems = (item.children || []).filter((child) => hasAccess(child));
    const hasActiveChild = childItems.some((child) => isActiveRoute(child.path));
    const pinned = isPinned(item.key);
    const badgeCount = getBadgeCount(item);
    // Normalize badge data
    let showBadge = false;
    let primaryBadge = 0;
    let secondaryBadge = 0;

    if (typeof badgeCount === 'number') {
      primaryBadge = badgeCount;
      showBadge = primaryBadge > 0;
    } else if (badgeCount && typeof badgeCount === 'object') {
      // Expect shape { primary, secondary, hasAny }
      primaryBadge = badgeCount.primary || 0;
      secondaryBadge = badgeCount.secondary || 0;
      showBadge = !!badgeCount.hasAny || primaryBadge > 0 || secondaryBadge > 0;
    }

    // Badge styles (colors) based on item key and badge role
    const primaryBadgeStyle = (() => {
      const base = { marginLeft: 8 };
      switch (item.key) {
        case 'preauth':
          // Pending PA
          return { ...base, backgroundColor: '#faad14', color: '#fff' }; // orange
        case 'client':
          // New clients
          return { ...base, backgroundColor: '#52c41a', color: '#fff' }; // green
        case 'claims':
          // Pending review claims
          return { ...base, backgroundColor: '#faad14', color: '#fff' }; // orange
        case 'appointments':
          return { ...base, backgroundColor: '#1890ff', color: '#fff' }; // blue
        default:
          return base;
      }
    })();

    const secondaryBadgeStyle = (() => {
      const base = { marginLeft: 4 };
      switch (item.key) {
        case 'preauth':
          // Queried PA
          return { ...base, backgroundColor: '#ff4d4f', color: '#fff' }; // red
        case 'client':
          // Updated clients
          return { ...base, backgroundColor: '#1890ff', color: '#fff' }; // blue
        default:
          return base;
      }
    })();

    const itemContent = (
      <div
        className={`sidebar-item ${isActive ? 'active' : ''} ${hasActiveChild ? 'has-active-child' : ''} ${isLoading ? 'loading' : ''}`}
        onClick={() => navigateToRoute(item.path)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigateToRoute(item.path);
          }
        }}
        aria-label={item.label}
        aria-current={isActive ? 'page' : undefined}
      >
        <div className="sidebar-item-icon">
          {railCollapsed && showBadge ? (
            <Badge count={primaryBadge} size="small" offset={[-5, 5]}>
              {item.icon}
            </Badge>
          ) : (
            item.icon
          )}
        </div>
        {!railCollapsed && (
          <span
            className="sidebar-item-label"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <span>{item.label}</span>
            {showBadge && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 2,
                  marginLeft: 8,
                }}
              >
                {badgeCount && typeof badgeCount === 'object' ? (
                  <>
                    {primaryBadge > 0 && (
                      <Badge
                        count={primaryBadge}
                        size="small"
                        style={primaryBadgeStyle}
                      />
                    )}
                    {secondaryBadge > 0 && (
                      <Badge
                        count={secondaryBadge}
                        size="small"
                        style={secondaryBadgeStyle}
                      />
                    )}
                  </>
                ) : (
                  <Badge
                    count={primaryBadge}
                    size="small"
                    style={primaryBadgeStyle}
                  />
                )}
              </div>
            )}
          </span>
        )}
      </div>
    );

    return (
      <div key={item.key} className={`sidebar-item-wrapper${childItems.length ? ' has-children' : ''}`}>
        {railCollapsed ? (
          <Tooltip
            title={item.label}
            placement="right"
            overlayStyle={{ zIndex: 1001 }}
          >
            {itemContent}
          </Tooltip>
        ) : (
          <>
            {itemContent}
            <button
              type="button"
              className={`sidebar-item-pin ${pinned ? 'is-pinned' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                togglePin(item.key);
              }}
              aria-label={pinned ? `Unpin ${item.label}` : `Pin ${item.label} to top`}
              title={pinned ? 'Unpin' : 'Pin to top'}
            >
              {pinned ? <PushpinFilled /> : <PushpinOutlined />}
            </button>
          </>
        )}
      </div>
    );
  }, [hasAccess, isActiveRoute, isLoading, navigateToRoute, railCollapsed, getBadgeCount, isPinned, togglePin]);

  // Enhanced navigation group renderer (exclusive accordion when expanded)
  const renderNavGroup = useCallback((group) => {
    const visibleItems = group.items.filter((item) => hasAccess(item));
    if (visibleItems.length === 0) return null;

    const isPinnedGroup = group.group === 'Pinned';
    const isOpen = railCollapsed || isPinnedGroup || openGroup === group.group;
    const hasActiveItem = flattenNavItems(group.items).some((item) => isActiveRoute(item.path));
    const groupBadgeTotal = visibleItems.reduce(
      (sum, item) => sum + badgeTotal(getBadgeCount(item)),
      0,
    );

    const toggleGroup = () => {
      if (railCollapsed) return;
      setOpenGroup((prev) => (prev === group.group ? null : group.group));
    };

    const groupId = `sidebar-group-${group.group.replace(/\s+/g, '-').toLowerCase()}`;

    return (
      <div
        key={group.group}
        className={`sidebar-group ${isOpen ? 'is-open' : ''} ${hasActiveItem ? 'has-active' : ''} ${groupBadgeTotal > 0 ? 'has-attention' : ''}`}
      >
        {!railCollapsed && (
          <button
            type="button"
            className="sidebar-group-title"
            onClick={toggleGroup}
            aria-expanded={isOpen}
            aria-controls={groupId}
          >
            <span className="sidebar-group-title-main">
              {isPinnedGroup && <PushpinFilled className="sidebar-group-pin-icon" aria-hidden />}
              <span>{group.group}</span>
              {groupBadgeTotal > 0 ? (
                <Badge
                  count={groupBadgeTotal}
                  size="small"
                  overflowCount={99}
                  className="sidebar-group-badge"
                />
              ) : null}
            </span>
            <DownOutlined className="sidebar-group-chevron" aria-hidden />
          </button>
        )}
        <div
          id={groupId}
          className={`sidebar-group-items ${isOpen ? 'is-open' : ''}`}
          aria-hidden={!isOpen}
        >
          <div className="sidebar-group-items-inner">
            {group.items.map(renderNavItem)}
          </div>
        </div>
      </div>
    );
  }, [hasAccess, railCollapsed, openGroup, renderNavItem, isActiveRoute, getBadgeCount]);

  if (!user) return null;

  return (
    <>
      {isMobile && mobileOpen && (
        <div
          className="sidebar-backdrop is-visible"
          onClick={toggleMobileMenu}
          role="button"
          tabIndex={-1}
          aria-label="Close menu"
        />
      )}

      <div className={`sidebar-container viewport-${viewport}${mobileOpen ? ' is-drawer-open' : ''}`}>
        <aside
          className={[
            'sidebar',
            railCollapsed ? 'collapsed' : '',
            mobileOpen ? 'mobile-open' : '',
            darkMode ? 'dark-mode' : '',
            `viewport-${viewport}`,
          ].filter(Boolean).join(' ')}
          role="navigation"
          aria-label="Main navigation"
          aria-hidden={isMobile && !mobileOpen ? true : undefined}
        >
          <header className="sidebar-header">
            <div className="sidebar-logo">
              {railCollapsed ? (
                <img src={collapsedLogo} alt="Claima" className="collapsed-logo" />
              ) : (
                <img src={logo} alt="Claima" />
              )}
            </div>
            {isMobile && (
              <button
                type="button"
                className="sidebar-drawer-close"
                onClick={toggleMobileMenu}
                aria-label="Close navigation"
              >
                <LeftOutlined />
              </button>
            )}
          </header>

          <nav className="sidebar-nav" role="menubar">
            {pinnedGroup && renderNavGroup(pinnedGroup)}
            {navigationConfig.map(renderNavGroup)}
          </nav>

          <footer className="sidebar-footer">
            <Dropdown
              menu={{ items: userMenuItems }}
              trigger={['click']}
              placement="topRight"
              overlayStyle={{ zIndex: 1100 }}
            >
              <div
                className="sidebar-user"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                  }
                }}
                aria-label="User menu"
              >
                <div className="sidebar-user-avatar">
                  <UserOutlined />
                </div>
                {!railCollapsed && (
                  <div className="sidebar-user-info">
                    <span className="sidebar-user-name">
                      {user.biodata?.firstName || user.staffId}
                    </span>
                    <span className="sidebar-user-role">
                      {user.role?.name || 'User'}
                    </span>
                  </div>
                )}
              </div>
            </Dropdown>
          </footer>
        </aside>

        {viewport === 'desktop' && (
          <div className="sidebar-collapse-controls">
            <button
              className="collapse-toggle"
              onClick={toggleCollapseHandler}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!collapsed}
            >
              <MenuOutlined />
            </button>
          </div>
        )}
      </div>

      <nav className={`horizontal-nav ${darkMode ? 'dark-mode' : ''} viewport-${viewport}`}>
        <div className="horizontal-nav-content">
          <div className="horizontal-nav-left">
            {isMobile && (
              <button
                type="button"
                className="topbar-menu-btn"
                onClick={toggleMobileMenu}
                aria-label="Open navigation"
                aria-expanded={mobileOpen}
              >
                <MenuOutlined />
              </button>
            )}
            {isMobile && (
              <img src={logo} alt="Claima" className="topbar-brand-logo" />
            )}
            {!isMobile && pageHeader?.left ? (
              <div className="horizontal-nav-page-header">{pageHeader.left}</div>
            ) : null}
          </div>
          <div className="horizontal-nav-right">
            {pageHeader?.right ? (
              <div className="horizontal-nav-page-header horizontal-nav-page-header--right">{pageHeader.right}</div>
            ) : null}
            {canGlobalSearch ? (
              <button
                type="button"
                className="horizontal-theme-toggle"
                onClick={openGlobalSearch}
                aria-label="Open search"
                title="Search (Ctrl+K or ⌘K)"
              >
                <SearchOutlined />
              </button>
            ) : null}
            <button
              className="horizontal-theme-toggle"
              onClick={toggleDarkMode}
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              title={darkMode ? 'Light Mode' : 'Dark Mode'}
            >
              {darkMode ? <BulbOutlined /> : <BulbFilled />}
            </button>

            {notifyV2Enabled ? (
              <NotificationBell className="horizontal-notification-wrapper" />
            ) : (
            <div className="horizontal-notification-wrapper">
              <button
                className="horizontal-notification-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowNavbarNotificationsDropdown(!showNavbarNotificationsDropdown);
                }}
                aria-label="Notifications"
                title="Notifications"
                aria-expanded={showNavbarNotificationsDropdown}
                aria-haspopup="true"
              >
                <BellOutlined />
                {unreadCount > 0 && (
                  <Badge count={unreadCount} size="small" className="horizontal-badge" overflowCount={99} />
                )}

                <div className={`horizontal-connection-indicator ${connected ? 'connected' : 'disconnected'}`}
                  title={connected ? 'Real-time notifications enabled' : 'Using offline mode (localStorage)'}
                >
                  <div className="horizontal-connection-indicator-dot" />
                </div>
              </button>

              {showNavbarNotificationsDropdown && (
                <NotificationsDropdown onClose={() => setShowNavbarNotificationsDropdown(false)} />
              )}
            </div>
            )}
          </div>
        </div>
      </nav>

      <Drawer
        title="My Profile"
        open={profileDrawerOpen}
        onClose={() => setProfileDrawerOpen(false)}
        width={420}
        destroyOnClose
      >
        <StaffProfileEditor
          initialValues={ownProfile || {}}
          submitting={profileSaving}
          onCancel={() => setProfileDrawerOpen(false)}
          onSubmit={handleSaveOwnProfile}
          submitLabel="Save profile"
          passwordMode="self"
        />
      </Drawer>
    </>
  );
};

export default Sidebar;
